import type {
  AppCategory,
  ConnectionStatus,
  ConnectionSummary,
  IntegrationAppSummary,
  ToolSideEffect,
} from '@coworker/contracts';
import { useState, type FormEvent } from 'react';

import { Badge, type BadgeProps } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  useConnectSession,
  useConnections,
  useCreateConnection,
  useDisconnectConnection,
  useSyncConnection,
} from '@/hooks/use-connections.js';
import { useApps } from '@/hooks/use-apps.js';
import { useConnectionTools, useSetConnectionTools } from '@/hooks/use-connection-tools.js';
import { useCurrentWorkspace } from '@/hooks/use-current-workspace.js';
import { ApiError, errorText } from '@/lib/api-error.js';

/**
 * Connected accounts - WEB-010, INT-006, INT-007.
 *
 * **Three steps, shown as three steps.** Add the connection, open the
 * provider's window, then say you are done. That is more clicks than a polished
 * flow would need, and it is honest about what is actually happening: there is
 * no webhook from Pipedream - no ticket owns one, ADR-0040 records it - so
 * something has to ask whether the connection completed. Making that the
 * person's button rather than a background timer means a stalled connection
 * looks stalled instead of looking like it is still working.
 *
 * The app is **picked, not typed** - COW-491. It was a text field when this
 * screen shipped, which meant knowing that Slack's provider slug is `slack_v2`
 * before you could connect it. `GET /v1/apps` now serves a curated catalogue
 * with a display name, a category and how many capabilities each app has, so
 * the picker can say which apps an employee could actually use. An app with
 * none is still offerable: connecting now and gaining tools later is ordinary.
 *
 * Not Pipedream's own catalogue - §6.1 puts it near three thousand apps, which
 * is not a dropdown.
 */
const STATUS_VARIANT: Readonly<Record<ConnectionStatus, BadgeProps['variant']>> = {
  PENDING: 'muted',
  AVAILABLE: 'default',
  REAUTH_REQUIRED: 'outline',
  REVOKED: 'muted',
};

const STATUS_LABEL: Readonly<Record<ConnectionStatus, string>> = {
  PENDING: 'Not connected yet',
  AVAILABLE: 'Connected',
  REAUTH_REQUIRED: 'Needs reauthorising',
  REVOKED: 'Disconnected',
};

/**
 * Category headings, in the order the dropdown shows them.
 *
 * The order itself comes from the database - `IntegrationAppRepository.listAll`
 * sorts by category then name - so every client renders the same list and this
 * is only the spelling.
 */
/**
 * Where an app with no category of ours goes.
 *
 * `category` became optional with the mirror (COW-492): the three thousand apps
 * copied from Pipedream have no opinion about our eight-value grouping, and
 * only the featured few carry one. The picker shows featured apps, so this
 * should be empty in practice - and a featured app that somehow lacks a
 * category renders under this heading rather than disappearing, which is the
 * right direction for the failure to go.
 */
const UNCATEGORISED = 'OTHER';
type PickerGroup = AppCategory | typeof UNCATEGORISED;

const CATEGORY_LABEL: Readonly<Record<PickerGroup, string>> = {
  OTHER: 'Other',
  CRM: 'CRM',
  COMMUNICATION: 'Communication',
  PRODUCTIVITY: 'Productivity',
  DEVELOPER: 'Developer tools',
  FINANCE: 'Finance',
  SUPPORT: 'Support',
  COMMERCE: 'Commerce',
  STORAGE: 'Storage',
};

/**
 * Groups the catalogue for `<optgroup>`, preserving the order it arrived in.
 *
 * A `Map` and not an object: insertion order is guaranteed for a Map and merely
 * conventional for string keys on an object, and the order here is the server's
 * decision rather than ours to re-derive.
 */
function grouped(
  apps: readonly IntegrationAppSummary[],
): ReadonlyMap<PickerGroup, IntegrationAppSummary[]> {
  const byCategory = new Map<PickerGroup, IntegrationAppSummary[]>();
  for (const app of apps) {
    const group: PickerGroup = app.category ?? UNCATEGORISED;
    byCategory.set(group, [...(byCategory.get(group) ?? []), app]);
  }
  return byCategory;
}

/**
 * What a failed disconnect actually means for the grant at the provider.
 *
 * **Not one sentence for every failure.** A draft appended "The connection is
 * still active." unconditionally, and a review rendered the API's own 404 body
 * through it: `no such connection in this workspace The connection is still
 * active.` - which is the opposite of the truth, and reachable without
 * contrivance, because `staleTime` is 30 s and another tab or another member
 * can revoke the row while this list is stale.
 *
 * The three cases differ in what is known:
 * - `NOT_FOUND` - the row is gone or already revoked. Nothing is active.
 * - `INTERNAL` / `CONFLICT` - the API refused to revoke *because* it could not
 *   end the grant, or because the row gained an account a `/sync` attached. The
 *   connection is live either way.
 * - `TIMEOUT`, or a gateway 401 - we stopped waiting or never arrived. The
 *   outcome is unknown, and saying either thing would be a guess.
 */
export function disconnectMessage(error: unknown): string {
  const base = errorText(error);
  const code = error instanceof ApiError ? error.code : 'INTERNAL';

  if (code === 'NOT_FOUND') return `${base} It may already have been disconnected.`;
  if (code === 'TIMEOUT' || code === 'UNAUTHENTICATED_AT_GATEWAY') {
    return `${base} Check the list before trying again — it may or may not have gone through.`;
  }
  return `${base} The connection is still active.`;
}

export function Integrations() {
  const workspace = useCurrentWorkspace();
  const { query } = useConnections();
  const apps = useApps();
  const create = useCreateConnection();
  const session = useConnectSession();
  const sync = useSyncConnection();
  const disconnect = useDisconnectConnection();

  /**
   * The last connect link, kept so it can be offered again.
   *
   * `expiresAt` exists on the wire precisely so the SPA can tell "took too
   * long" from "something is broken" - `http/integration.ts` says so - and a
   * draft ignored it, which made an expired token read as `changed: false` and
   * produced advice that could not work: finish it in a window whose token is
   * already dead.
   */
  const [link, setLink] = useState<
    { connectionId: string; url: string; expiresAt: string } | undefined
  >(undefined);

  const [appKey, setAppKey] = useState('');
  const [displayName, setDisplayName] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = displayName.trim();
    create.mutate(
      {
        appKey: appKey.trim(),
        provider: 'PIPEDREAM',
        // Omitted rather than sent empty: the API falls back to the app key, so
        // a blank field means "you choose" rather than "call it nothing".
        ...(trimmedName === '' ? {} : { displayName: trimmedName }),
      },
      {
        onSuccess: () => {
          setAppKey('');
          setDisplayName('');
        },
      },
    );
  }

  /**
   * Opens the provider's window for one connection.
   *
   * `window.open` with the URL the API returned, never one assembled here -
   * Pipedream owns that address and has changed it before. A blocked popup is
   * reported rather than swallowed: the person clicked, so silence would look
   * like the app was broken.
   */
  /**
   * Which rows have a request in flight.
   *
   * **A set, because every row shares one mutation object.** Two drafts got
   * this wrong: the first compared a boolean to a connection id and matched
   * nothing, so no button ever disabled; the second read the mutation's
   * `variables`, which is single-slot - clicking Disconnect on a second row
   * re-enabled the first while its request was still outstanding, and a review
   * measured two `DELETE`s in flight with one button live. That extra click is
   * how the 404 above becomes reachable.
   */
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());

  function act(connectionId: string, run: (id: string) => Promise<unknown>): void {
    // A new Set each time rather than mutating the one in state.
    setBusy((current) => new Set(current).add(connectionId));
    run(connectionId)
      // The mutation records its own error; this only clears the flag, and an
      // unhandled rejection here would be noise on a path that is already
      // reported.
      .catch(() => undefined)
      .finally(() => {
        setBusy((current) => {
          const next = new Set(current);
          next.delete(connectionId);
          return next;
        });
      });
  }

  function connect(connectionId: string): void {
    act(connectionId, async (id) => {
      const opened = await session.mutateAsync(id);
      setLink({ connectionId: id, url: opened.connectUrl, expiresAt: opened.expiresAt });
      // Best effort. `noopener` makes `window.open` return null by
      // specification, so its result says nothing about whether a popup was
      // blocked - and the call happens after a round trip this client allows
      // 12 s for, by which time the click's transient activation has lapsed in
      // every current browser. So the link below is rendered either way, and it
      // is the thing that always works.
      window.open(opened.connectUrl, '_blank', 'noopener,noreferrer');
      return opened;
    });
  }

  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-8">
      <h1 className="text-2xl font-semibold">Integrations</h1>

      {workspace === undefined ? (
        // The same real state `Employees` has: the session carries INVITED and
        // SUSPENDED memberships too, and neither authorizes anything.
        <p className="text-sm text-muted-foreground">
          You are not an active member of any workspace yet. Once an invitation is accepted, your
          connections appear here.
        </p>
      ) : (
        <>
          <ConnectionList
            query={query}
            onConnect={connect}
            onSync={(id) => act(id, sync.mutateAsync)}
            onDisconnect={(id) => act(id, disconnect.mutateAsync)}
            busy={busy}
          />

          {session.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(session.error)}
            </p>
          ) : null}
          {sync.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(sync.error)}
            </p>
          ) : null}
          {disconnect.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {disconnectMessage(disconnect.error)}
            </p>
          ) : null}

          {sync.isSuccess && !sync.data.changed ? (
            <p role="status" className="text-sm text-muted-foreground">
              {link !== undefined && Date.parse(link.expiresAt) <= Date.now()
                ? 'That connect link has expired. Press Connect again to start a new one.'
                : 'Nothing has arrived from the provider yet. Finish the connection in the other window, then check again.'}
            </p>
          ) : null}

          {link !== undefined && Date.parse(link.expiresAt) > Date.now() ? (
            // Rendered whether or not the popup opened, because nothing can
            // tell us. A second click is always allowed by the browser, so this
            // anchor works where the automatic open does not.
            <p className="text-sm text-muted-foreground">
              If nothing opened,{' '}
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4"
              >
                open the provider in a new tab
              </a>
              .
            </p>
          ) : null}

          <form onSubmit={onSubmit} className="flex flex-col gap-3 border-t border-border pt-6">
            <h2 className="text-lg font-semibold">Add a connection</h2>
            <p className="text-sm text-muted-foreground">
              Adding one records it here. Connecting it opens the provider, where you sign in to the
              app itself — we never see those credentials.
            </p>

            <label className="flex flex-col gap-1 text-sm">
              App
              <select
                value={appKey}
                onChange={(event) => setAppKey(event.target.value)}
                required
                disabled={apps.query.isPending}
                className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
              >
                <option value="" disabled>
                  {apps.query.isPending ? 'Loading apps…' : 'Choose an app'}
                </option>
                {[...grouped(apps.query.data?.apps ?? [])].map(([category, inCategory]) => (
                  <optgroup key={category} label={CATEGORY_LABEL[category]}>
                    {inCategory.map((app) => (
                      <option key={app.appKey} value={app.appKey}>
                        {/* An app with no capabilities is still offerable -
                            connecting now and gaining tools later is ordinary -
                            but saying so beats letting somebody connect Dropbox
                            and wait for an employee that cannot use it. */}
                        {app.name}
                        {app.capabilityCount === 0 ? ' — no tools yet' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {apps.query.isError ? (
                <span role="alert" className="text-xs text-destructive">
                  The app list could not be loaded. {errorText(apps.query.error)}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Your employee can only use an app that has tools for it.
                </span>
              )}
            </label>

            <label className="flex flex-col gap-1 text-sm">
              Label <span className="text-xs text-muted-foreground">(optional)</span>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={120}
                placeholder="Sales HubSpot"
                className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
              />
              <span className="text-xs text-muted-foreground">
                A name you will recognise in the list. One connection per app for now.
              </span>
            </label>

            {create.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {errorText(create.error)}
              </p>
            ) : null}

            {/* Disabled while the catalogue loads, and not only while creating.
                `required` on the select does not cover that window: a *disabled*
                control is barred from constraint validation entirely -
                `willValidate` is false and `checkValidity()` returns true - so
                a click during the load submitted `appKey: ''` and got a 422.
                Measured in jsdom by a review. */}
            <Button
              type="submit"
              disabled={create.isPending || apps.query.isPending}
              className="self-start"
            >
              {create.isPending ? 'Adding…' : 'Add'}
            </Button>
          </form>
        </>
      )}
    </section>
  );
}

interface ConnectionListProps {
  readonly query: ReturnType<typeof useConnections>['query'];
  readonly onConnect: (connectionId: string) => void;
  readonly onSync: (connectionId: string) => void;
  readonly onDisconnect: (connectionId: string) => void;
  readonly busy: ReadonlySet<string>;
}

function ConnectionList({ query, onConnect, onSync, onDisconnect, busy }: ConnectionListProps) {
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading connections…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { connections } = query.data;
  // Disconnected ones stay in the database so a tool execution that referenced
  // one is still explicable, but showing them here would be a list of things
  // somebody has already dismissed.
  const active = connections.filter((connection) => connection.status !== 'REVOKED');

  if (active.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No connections yet. Add one below to let your employees work in the apps you already use.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {active.map((connection) => (
        <ConnectionCard
          key={connection.id}
          connection={connection}
          onConnect={() => onConnect(connection.id)}
          onSync={() => onSync(connection.id)}
          onDisconnect={() => onDisconnect(connection.id)}
          busy={busy.has(connection.id)}
        />
      ))}
    </ul>
  );
}

interface ConnectionCardProps {
  readonly connection: ConnectionSummary;
  readonly onConnect: () => void;
  readonly onSync: () => void;
  readonly onDisconnect: () => void;
  readonly busy: boolean;
}

function ConnectionCard({
  connection,
  onConnect,
  onSync,
  onDisconnect,
  busy,
}: ConnectionCardProps) {
  const pending = connection.status === 'PENDING';
  // Closed until asked for. The list costs a request per connection, and a
  // workspace with eight connections would otherwise make eight on every visit
  // to a screen most people open to connect a ninth.
  const [showTools, setShowTools] = useState(false);

  return (
    <li className="flex flex-col gap-3 rounded-[--radius] border border-border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-medium">{connection.displayName}</span>
          <span className="text-xs text-muted-foreground">{connection.appKey}</span>
          {connection.externalAccountId === undefined ? null : (
            <span className="text-xs text-muted-foreground">{connection.externalAccountId}</span>
          )}
        </div>
        <Badge variant={STATUS_VARIANT[connection.status]}>{STATUS_LABEL[connection.status]}</Badge>
      </div>

      {connection.scopes.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Permissions granted: {connection.scopes.join(', ')}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {pending ? (
          <>
            <Button size="sm" onClick={onConnect} disabled={busy}>
              Connect
            </Button>
            {/* Separate from Connect, because the person has to do something in
                another window in between and only they know when it is done. */}
            <Button variant="outline" size="sm" onClick={onSync} disabled={busy}>
              I’ve finished connecting
            </Button>
          </>
        ) : null}
        {/* Only once the provider has granted something. A PENDING connection
            has no account behind it, so its app's tools cannot be listed and
            the mirror is empty by construction. */}
        {pending ? null : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTools((open) => !open)}
            aria-expanded={showTools}
          >
            {showTools ? 'Hide tools' : 'Choose tools'}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onDisconnect} disabled={busy}>
          Disconnect
        </Button>
      </div>

      {showTools ? <ToolPicker connectionId={connection.id} appKey={connection.appKey} /> : null}
    </li>
  );
}

/**
 * Which of the app's tools this connection may use - COW-493.
 *
 * **Nothing is saved until Save is pressed.** The draft lives here and the
 * server holds the committed answer; a checkbox that wrote on every click would
 * turn one person's indecision into a dozen requests, and each one is a
 * permission change somebody may be watching.
 *
 * Mounted only when the panel is open, so the query is the panel's own and the
 * request happens on first expand rather than on screen load.
 */
function ToolPicker({ connectionId, appKey }: { connectionId: string; appKey: string }) {
  const { query } = useConnectionTools(connectionId);
  const save = useSetConnectionTools(connectionId);

  // `undefined` means "not edited yet", which is different from "nothing is
  // switched off". Without the distinction the panel would show every tool
  // enabled for the instant before the answer arrives.
  const [draft, setDraft] = useState<ReadonlySet<string> | undefined>(undefined);

  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading tools…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-2">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { tools, mirrorEmpty } = query.data;

  if (mirrorEmpty) {
    // An app with nothing to offer and one whose listing has not run look the
    // same from here, so the API distinguishes them and this says which.
    return (
      <p className="text-sm text-muted-foreground">
        No tools have been read from {appKey} yet. They arrive the first time somebody connects it,
        and an administrator can refresh them.
      </p>
    );
  }

  if (tools.length === 0) {
    // **The third state**, and the reason `mirrorEmpty` is counted over the
    // whole mirror rather than over what is offered: tools were read once and
    // the provider has since withdrawn every one of them. Telling somebody to
    // refresh would send them to run a sync that has already run. A review
    // found the screen rendering the intro sentence above an empty list.
    return (
      <p className="text-sm text-muted-foreground">
        {appKey} no longer offers any of the tools we know about. Nothing here can be chosen until
        it offers some again.
      </p>
    );
  }

  const disabled = draft ?? new Set(tools.filter((tool) => !tool.enabled).map((t) => t.toolName));
  const dirty = draft !== undefined;

  function toggle(toolName: string): void {
    setDraft((current) => {
      const next = new Set(current ?? disabled);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-3 border-t border-border pt-3">
      <p className="text-xs text-muted-foreground">
        Everything is on by default. Switch off anything this account should not do — employees do
        not use these tools yet, so this takes effect when they start.
      </p>

      <ul className="flex flex-col gap-2">
        {tools.map((tool) => (
          <li key={tool.toolName} className="flex items-start gap-2">
            <input
              type="checkbox"
              id={`${connectionId}-${tool.toolName}`}
              className="mt-1"
              checked={!disabled.has(tool.toolName)}
              onChange={() => toggle(tool.toolName)}
            />
            <label htmlFor={`${connectionId}-${tool.toolName}`} className="flex flex-col gap-0.5">
              <span className="text-sm">{tool.description ?? tool.toolName}</span>
              <span className="text-xs text-muted-foreground">
                {tool.toolName} · {SIDE_EFFECT_LABEL[tool.sideEffect]}
                {tool.classification === 'UNCLASSIFIED' ? ' · unclassified' : ''}
              </span>
            </label>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate([...disabled], { onSuccess: () => setDraft(undefined) });
          }}
        >
          Save
        </Button>
        {save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {errorText(save.error)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a tool does to the world, in words rather than in the vocabulary.
 *
 * The person choosing has to be able to tell reading from writing, and
 * `EXTERNAL_COMMUNICATION` is not a phrase anybody says. The order of
 * `TOOL_SIDE_EFFECTS` runs harmless to irreversible; these labels keep that
 * reading.
 */
const SIDE_EFFECT_LABEL: Readonly<Record<ToolSideEffect, string>> = {
  READ_ONLY: 'reads only',
  DRAFT: 'prepares a draft',
  REVERSIBLE_WRITE: 'writes, undoably',
  EXTERNAL_COMMUNICATION: 'sends something out',
  FINANCIAL: 'moves money',
  CODE_DEPLOY: 'deploys code',
  DESTRUCTIVE: 'deletes',
  SECURITY: 'changes security',
};
